import zod from "zod";

import { zodDateStringStartingYear } from "../../validation/common.js";

const REQUIRED = "Required";

const smsFormAddressSchema = zod.object({
  line1: zod.string().min(1, REQUIRED),
  line2: zod.string().optional().nullable(),
  postcode: zod
    .string()
    .min(1, REQUIRED)
    .refine(
      (value) => {
        if ([...value].filter((char) => char === " ").length === 1) {
          return true;
        }
      },
      { message: "You must put one space in the postcode" },
    ),
  townOrCity: zod.string().min(1, REQUIRED),
  county: zod.string().min(1, REQUIRED).optional().nullable(),
});

const smsFormAddressWithCountrySchema = smsFormAddressSchema.extend({
  country: zod.string().min(1, REQUIRED),
});

const smsFormAddressWithPhoneNumberAndCountry =
  smsFormAddressWithCountrySchema.extend({
    phoneNumber: zod.string().min(1, REQUIRED),
  });

const smsFormEmploymentAddress = smsFormAddressWithCountrySchema.extend({
  phoneNumber: zod.string({ invalid_type_error: REQUIRED }).min(1, REQUIRED),
  email: zod.string({ invalid_type_error: REQUIRED }).email().min(1, REQUIRED),
  dateOfBirth: zodDateStringStartingYear,
  nationalityCountry: zod
    .string({ invalid_type_error: REQUIRED })
    .min(1, REQUIRED),
  positionWithinOrganisation: zod
    .string({ invalid_type_error: REQUIRED })
    .min(1, REQUIRED),
});

// const smsFormPreviousTradingName = zod.object({
//   tradingName: zod.string().min(1, REQUIRED),
//   address: smsFormAddressWithCountrySchema,
//   phoneNumber: zod.string().min(1, REQUIRED),
//   website: zod.string().min(1, REQUIRED),
//   tradingFrom: zodDateStringStartingYear,
//   tradingTo: zodDateStringStartingYear,
// });

// const smsFormAccreditedBody = zod.object({
//   bodyName: zod.string().min(1, REQUIRED),
//   bodyNumber: zod.number(),
//   expiryDate: zodDateStringStartingYear,
// });

const smsFormContactSchema = zod.object({
  title: zod.string({ invalid_type_error: REQUIRED }).min(1, REQUIRED),
  otherTitle: zod.string().nullable().optional(),
  firstName: zod.string({ invalid_type_error: REQUIRED }).min(1, REQUIRED),
  lastName: zod.string({ invalid_type_error: REQUIRED }).min(1, REQUIRED),
  previousName: zod.string().nullable().optional(),
  employmentAddress: smsFormEmploymentAddress,
  // hasNationalInsuranceNumber: zod.boolean(),
  nationalInsuranceNumber: zod
    .string({ invalid_type_error: REQUIRED })
    .min(1, { message: REQUIRED }),
  // nationalInsuranceNumberExemption: zod.string().nullable().optional(),
  // isNonSettledUnderImmigrationControl: zod.boolean(),
  // immigrationStatus: zod.string().nullable().optional(),
  // homeOfficeReference: zod.string().nullable().optional(),
  // dateCurrentLeaveToEnterOrLeaveEnds: zodDateStringStartingYear
  //   .nullable()
  //   .optional(),
  // passportNumber: zod.string().nullable().optional(),
  // hasOfficerBeenConvictedOrBankrupt: zod.boolean(),
  // convictionOrBankruptDetails: zod.string().nullable().optional(),
});

const smsFormKeyContactSchema = smsFormContactSchema.extend({
  // isKeyContact: zod.boolean(),
});

const getContactSchemaAdditionalChecks = (schema: zod.ZodObject<any>) => {
  return schema.superRefine((data, ctx) => {
    if (data.title.trim() === "ZZ" && !data.otherTitle) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: "Required",
        path: ["otherTitle"],
      });
    }
    if (data.nationalInsuranceNumber) {
      const ninRegex = /^[a-zA-Z]{2}\d{6}[a-zA-Z]{1}$/;
      if (data.nationalInsuranceNumber.split(" ").length > 1) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: "National Insurance Number must not contain spaces",
          path: ["nationalInsuranceNumber"],
        });
      } else if (!ninRegex.test(data.nationalInsuranceNumber)) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: "Invalid National Insurance Number",
          path: ["nationalInsuranceNumber"],
        });
      }
    }

    // if (data.hasNationalInsuranceNumber && !data.nationalInsuranceNumber) {
    //   ctx.addIssue({
    //     code: zod.ZodIssueCode.custom,
    //     message: "Required",
    //     path: ["nationalInsuranceNumber"],
    //   });
    // }

    // if (
    //   !data.hasNationalInsuranceNumber &&
    //   !data.nationalInsuranceNumberExemption
    // ) {
    //   ctx.addIssue({
    //     code: zod.ZodIssueCode.custom,
    //     message: "Required",
    //     path: ["nationalInsuranceNumberExemption"],
    //   });
    // }

    // if (
    //   data.hasOfficerBeenConvictedOrBankrupt &&
    //   !data.convictionOrBankruptDetails
    // ) {
    //   ctx.addIssue({
    //     code: zod.ZodIssueCode.custom,
    //     message: "Required",
    //     path: ["convictionOrBankruptDetails"],
    //   });
    // }
    // if (data.isNonSettledUnderImmigrationControl) {
    //   if (!data.passportNumber) {
    //     ctx.addIssue({
    //       code: zod.ZodIssueCode.custom,
    //       message: "Required",
    //       path: ["passportNumber"],
    //     });
    //   }
    //   if (data.passportNumber.length < 9) {
    //     ctx.addIssue({
    //       code: zod.ZodIssueCode.custom,
    //       message: "Passport number must be 9 characters long",
    //       path: ["passportNumber"],
    //     });
    //   }
    //   if (!data.immigrationStatus) {
    //     ctx.addIssue({
    //       code: zod.ZodIssueCode.custom,
    //       message: "Required",
    //       path: ["immigrationStatus"],
    //     });
    //   }
    //   if (!data.homeOfficeReference) {
    //     ctx.addIssue({
    //       code: zod.ZodIssueCode.custom,
    //       message: "Required",
    //       path: ["homeOfficeReference"],
    //     });
    //   }
    //   if (!data.dateCurrentLeaveToEnterOrLeaveEnds) {
    //     ctx.addIssue({
    //       code: zod.ZodIssueCode.custom,
    //       message: "Required",
    //       path: ["dateCurrentLeaveToEnterOrLeaveEnds"],
    //     });
    //   }
    // }
  });
};

export const smsFormValidationSchema = zod
  .object({
    organisationLegalName: zod.string().min(1, REQUIRED),
    organisationLegalAddress: smsFormAddressWithPhoneNumberAndCountry,
    // organisationTradingName: zod.string().min(1, REQUIRED),
    // organisationTradingAddress: smsFormAddressWithPhoneNumberAndCountry,
    // previousTradingNames: zod.array(smsFormPreviousTradingName),
    operatingRegion: zod
      .string({ invalid_type_error: REQUIRED })
      .min(1, REQUIRED),
    currentEmployeesNumber: zod.number({ invalid_type_error: REQUIRED }),
    currentlyEmployingMigrantWorkers: zod.boolean(),
    currentlyEmployedMigrantWorkersNum: zod
      .number({ invalid_type_error: REQUIRED })
      .nullable()
      .optional(),
    typeOfBody: zod.string({ invalid_type_error: REQUIRED }).min(1, REQUIRED),
    typeOfBodyDetails: zod
      .string({ invalid_type_error: REQUIRED })
      .nullable()
      .optional(),
    holdingCharitableStatus: zod.boolean(),
    charityRegStatus: zod.string().nullable().optional(),
    charityRegNumber: zod.string().nullable().optional(),
    typeOfOrganisation: zod
      .string({ invalid_type_error: REQUIRED })
      .min(1, REQUIRED),
    companiesHouseRegistrationNumber: zod.string().nullable().optional(),
    organisationTradingPeriod: zod
      .string({ invalid_type_error: REQUIRED })
      .min(1, REQUIRED),
    isVatRegistered: zod.boolean(),
    vatRegistrationNumber: zod.string().nullable().optional(),
    accountsOfficeReferenceNumber: zod
      .string({ invalid_type_error: REQUIRED })
      .min(1, REQUIRED),
    payeReferenceNumbers: zod
      .array(
        zod.object({
          payeReference: zod
            .string({ invalid_type_error: REQUIRED })
            .min(1, REQUIRED),
        }),
      )
      .min(1, REQUIRED),
    // registeredOnStockExchange: zod.boolean(),
    // stockExchangeName: zod.string().nullable().optional(),
    // registeredToAccreditedBodies: zod.array(smsFormAccreditedBody),
    numberOfCos: zod.number({ invalid_type_error: REQUIRED }),
    reasonForNumberOfCos: zod
      .string({ invalid_type_error: REQUIRED })
      .min(1, REQUIRED),

    cqcNumber: zod.string().min(1, REQUIRED),
    organisationAuthorisingOfficer: getContactSchemaAdditionalChecks(
      smsFormKeyContactSchema,
    ),
    // keyContact: getContactSchemaAdditionalChecks(smsFormContactSchema)
    //   .nullable()
    //   .optional(),
    // levelOneUsers: zod.array(
    //   getContactSchemaAdditionalChecks(smsFormContactSchema),
    // ),
    agreeWithAllTheStatementsDate: zodDateStringStartingYear,
    // documents: zod.object({
    //   "sponsorAppDocs:priDoc:2:provideSingle": zod.boolean(),
    //   "sponsorAppDocs:priDoc:3:provideSingle": zod.boolean(),
    //   "sponsorAppDocs:secDoc:0:provideSingle": zod.boolean(),
    //   "sponsorAppDocs:secDoc:1:provideSingle": zod.boolean(),
    //   "sponsorAppDocs:secDoc:2:provideSingle": zod.boolean(),
    //   "sponsorAppDocs:secDoc:3:provideSingle": zod.boolean(),
    //   "sponsorAppDocs:secDoc:4:provideSingle": zod.boolean(),
    //   "sponsorAppDocs:secDoc:5:provideSingle": zod.boolean(),
    //   "sponsorAppDocs:secDoc:6:provideSingle": zod.boolean(),
    //   "sponsorAppDocs:secDoc:7:provideSingle": zod.boolean(),
    // }),
    usingAgencyStaff: zod.boolean().optional(),
    officeStaffCarryingOutCareCalls: zod.boolean().optional(),
    numberOfPartTimeCarers: zod.number().nullable().optional(),
    numberOfFullTimeCarers: zod.number().nullable().optional(),
    numberOfCarersResigned: zod.number().nullable().optional(),
    triedToRecruitFromWithinTheUK: zod.string().nullable().optional(),
    whyApplyingForSponsorLicence: zod.string().nullable().optional(),
    hasReadGuidance: zod.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (
      data.currentlyEmployingMigrantWorkers &&
      !data.currentlyEmployedMigrantWorkersNum
    ) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: "Please provide number of migrant workers employed",
        path: ["currentlyEmployedMigrantWorkersNum"],
      });
    }
    if (data.hasReadGuidance !== true) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: "You must read the guidance before submitting",
        path: ["hasReadGuidance"],
      });
    }

    // temporarily removed in case we find solution to different document types required

    // const additionalDocuments = [
    //   data.documents["sponsorAppDocs:secDoc:0:provideSingle"],
    //   data.documents["sponsorAppDocs:secDoc:1:provideSingle"],
    //   data.documents["sponsorAppDocs:secDoc:2:provideSingle"],
    //   data.documents["sponsorAppDocs:secDoc:3:provideSingle"],
    //   data.documents["sponsorAppDocs:secDoc:4:provideSingle"],
    //   data.documents["sponsorAppDocs:secDoc:5:provideSingle"],
    //   data.documents["sponsorAppDocs:secDoc:6:provideSingle"],
    //   data.documents["sponsorAppDocs:secDoc:7:provideSingle"],
    // ].filter((value) => value).length;

    // if (additionalDocuments < 2) {
    //   ctx.addIssue({
    //     code: zod.ZodIssueCode.custom,
    //     message: "You need at least 2 additional documents",
    //     path: ["documents", "sponsorAppDocs:secDoc:7:provideSingle"],
    //   });
    // }
    // if (!data.organisationAuthorisingOfficer.isKeyContact && !data.keyContact) {
    //   ctx.addIssue({
    //     code: zod.ZodIssueCode.custom,
    //     message: "Required",
    //     path: ["keyContact"],
    //   });
    // }
    if (data.isVatRegistered && !data.vatRegistrationNumber?.length) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: "Required",
        path: ["vatRegistrationNumber"],
      });
    }

    // if (data.registeredOnStockExchange && !data.stockExchangeName?.length) {
    //   ctx.addIssue({
    //     code: zod.ZodIssueCode.custom,
    //     message: "Required",
    //     path: ["stockExchangeName"],
    //   });
    // }

    if (
      ["HEADUKBR", "GRPBR", "OTHER"].includes(data.typeOfBody) &&
      !data.typeOfBodyDetails
    ) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: "Required",
        path: ["typeOfBodyDetails"],
      });
    }

    if (data.holdingCharitableStatus && !data.charityRegStatus) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: "Required",
        path: ["charityRegStatus"],
      });
    }
    if (data.holdingCharitableStatus && !data.charityRegNumber) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: "Required",
        path: ["charityRegNumber"],
      });
    }
  });
